import type { IsoTimestamp } from './common.js';
import type { LeadCallOutcome, LeadCallRecording, LeadCallSummary, LeadCallTranscript } from './leadCall.js';

// SPEED TO LEAD, Stage 2: the lead intake outbox, the per-club settings and the number pools.
// Plan: _architect/run-2026-10-22-speed-to-lead-plan.md. Decisions log wins where they differ.
//
// ⚠️ EVERYTHING HERE DEFAULTS OFF. A club with no settings doc has Sam off and calls off; no club is
// enabled by code, a migration or a script.

// ---------------------------------------------------------------------------
// the intake outbox
// ---------------------------------------------------------------------------

export type LeadIntakeStatus = 'pending' | 'processing' | 'done' | 'failed' | 'dead';

/** What intake did for a lead. Recorded on the event so "why did nothing happen?" is answerable. */
export type LeadIntakeResultKind =
  /** The club has Sam off (or no settings): the lead was recorded and nothing else happened. */
  | 'no_channel'
  /** Sam's conversation was created and the opener went through the normal send path (gates apply). */
  | 'sam_started'
  /** Conversation created in standby: no opener until the fallback releases it. */
  | 'sam_standby'
  /** The phone already had an open conversation at this club: the submission was attached, no second opener. */
  | 'attached_to_open_conversation'
  /** Marked "Do not contact" at this club. */
  | 'do_not_contact'
  /** The submission has no phone we can text. */
  | 'no_phone'
  /** The phone has an open conversation at another club/bot, so no second one was started. */
  | 'open_elsewhere'
  /** The conversation could not be started (e.g. the phone opted out of SMS, or the bot refused). The detail says why. */
  | 'blocked'
  /** The club's bot is missing, inactive or belongs to another club. */
  | 'no_bot'
  /** The submission could not be read (deleted, or not a live lead). */
  | 'no_submission'
  /** Stage 3: the call chain was started (Sam may or may not also be on). The detail says what the chain did. */
  | 'call_started';

export interface LeadIntakeResult {
  kind: LeadIntakeResultKind;
  detail: string;
}

/**
 * ⚠️ IDS ONLY, no name and no phone: the relay writes this, and the agency app reads the submission
 * itself. Doc id = the leadSubmissionId, so a second event for one submission cannot exist.
 */
export interface LeadIntakeEvent {
  leadSubmissionId: string;
  accountId: string;
  /** The submission's own receivedAt: every speed-to-lead figure is measured from here. */
  receivedAt: IsoTimestamp;
  createdAt: IsoTimestamp;
  status: LeadIntakeStatus;
  attempts: number;
  /** When the sweep may next take it. Null once done or dead. A processing event carries its lease expiry. */
  nextAttemptAt: IsoTimestamp | null;
  /** First time the agency app picked it up (the hop latency is intakeReceivedAt - receivedAt). */
  intakeReceivedAt: IsoTimestamp | null;
  intakeProcessedAt: IsoTimestamp | null;
  lastError: string | null;
  result: LeadIntakeResult | null;
  botConversationId: string | null;
  /** Both channels only: when Sam may text if the call has not reached the lead. Null once resolved. */
  samFallbackDueAt: IsoTimestamp | null;
  samFallbackResolvedAt: IsoTimestamp | null;
  /** `dead` = leased FALLBACK_MAX_ATTEMPTS times without resolving (Stage 4E): stopped, logged and alerted. */
  samFallbackOutcome: 'released' | 'cancelled_reached' | 'not_needed' | 'dead' | null;
  /** Leases taken on this fallback without it resolving (Stage 4E). Absent on older events = 0. */
  samFallbackAttempts?: number;
  samFallbackLeaseUntil: IsoTimestamp | null;
  /**
   * STAGE 3 HOOKS. The call engine only has to set these (markLeadReached / markCallInProgress):
   * a reached lead cancels the fallback for good, and a call still in progress defers it.
   */
  leadReachedAt: IsoTimestamp | null;
  callInProgressUntil: IsoTimestamp | null;
  /** Real Timestamp for a Firestore TTL policy. */
  expiresAt: Date;
}

// ---------------------------------------------------------------------------
// per-club settings
// ---------------------------------------------------------------------------

// ⚠️ ONE VALUE since 2026-10-23: a machine verdict no longer hangs up or leaves a voicemail (staff stay on and
// decide), so `staff_voicemail` is gone. Stored `staff_voicemail` values read as `hang_up` (no migration needed).
// `hang_up` governs no-answer, busy, failed and rejected only.
export type NoAnswerAction = 'hang_up';

export interface SpeedToLeadStaffEntry {
  /** A BusinessProfile.team[] id. The name and mobile are read from there, never copied. */
  teamMemberId: string;
  receivesLeadCalls: boolean;
}

/**
 * INBOUND CALL FORWARDING (Stage 2.5). A lead who calls back the number Sam texted from rings the club's
 * PRIMARY number, with a whisper. ⚠️ `enabled` DEFAULTS FALSE: off (or no primary number) means the caller
 * hears a short fixed message, the call is logged and the club is alerted. (Stage 3B: an ACCEPTED call is recorded
 * by default; see CallsSettings.)
 */
export interface InboundCallsSettings {
  enabled: boolean;
  /** AU E.164 mobile or landline; never one of our own numbers (that would loop). Null = none set. */
  primaryNumber: string | null;
  /** After the primary number, ring each enabled staff member in order. */
  thenStaffList: boolean;
  ringSeconds: number;
  /** Outside the club's staffed hours: the fixed message, no ringing. */
  respectStaffedHours: boolean;
  /** The whisper template (heard by staff). Null = the default. The key prompt is added by the system. */
  whisperMessage: string | null;
  /** What the caller hears when nobody takes the call. Null = the default. */
  missedCallMessage: string | null;
  /** Kyck-only. When true the club sees its two messages read-only in the portal (enforced on the server). */
  messagesLockedByKyck: boolean;
}

export const INBOUND_CALLS_DEFAULTS: InboundCallsSettings = {
  enabled: false,
  primaryNumber: null,
  thenStaffList: false,
  // 20, not 25: most mobiles divert to voicemail at about 20-25 seconds (and the callee must press a key anyway).
  ringSeconds: 20,
  respectStaffedHours: true,
  whisperMessage: null,
  missedCallMessage: null,
  messagesLockedByKyck: false,
};

/**
 * OUTBOUND CALLS (Stage 3) and RECORDING (Stage 3B), per club. Kyck-only (the portal never writes this block).
 * ⚠️ `recordCalls` and `transcribeCalls` default TRUE (Scott, 2026-10-22: no recording notice is played; telling
 * people is the client's responsibility). They apply to Speed to Lead calls AND inbound forwarded calls.
 */
export interface CallsSettings {
  /** Outside staffed hours with Sam off: schedule the chain for the next opening when it is within 18 hours. */
  callAtNextOpening: boolean;
  /** Live chains for this club at once. At the cap, the Sam fallback fires at once (no queueing). */
  maxConcurrentCalls: number;
  maxCallsPerDay: number;
  recordCalls: boolean;
  /** Only meaningful while recordCalls is on. */
  transcribeCalls: boolean;
  recordingRetentionDays: number;
  /** The outbound staff whisper template (heard by staff before the key press). Null = the default. */
  whisperMessage: string | null;
}

export const CALLS_DEFAULTS: CallsSettings = {
  callAtNextOpening: true,
  maxConcurrentCalls: 3,
  maxCallsPerDay: 50,
  recordCalls: true,
  transcribeCalls: true,
  recordingRetentionDays: 90,
  whisperMessage: null,
};

export interface SpeedToLeadSettings {
  /** Doc id = accountId: one per club, structurally. */
  accountId: string;
  /** Sam (the SMS assistant) answers this club's Meta leads. ⚠️ Default FALSE. */
  samEnabled: boolean;
  /**
   * Speed to Lead calls. ⚠️ OFF BY DEFAULT and turned on only by a person, one club at a time. Even then NO
   * call is placed unless the global kill switch SPEED_TO_LEAD_CALLS_ENABLED is "true".
   */
  speedToLeadEnabled: boolean;
  /** Which of the club's bots answers. Null = the club's active bot. */
  botConfigId: string | null;
  respectStaffedHours: boolean;
  /** X: minutes before Sam texts if the call has not reached the lead (both channels on). */
  fallbackMinutes: number;
  /** A call this long or longer counts as a quality call on the dashboards. */
  qualityCallSeconds: number;
  noAnswerAction: NoAnswerAction;
  /** Null = no retry. */
  retryAfterMinutes: number | null;
  staffRingSeconds: number;
  roundTwoDelaySeconds: number;
  maxRounds: number;
  staffList: SpeedToLeadStaffEntry[];
  /** Inbound call forwarding (Stage 2.5). Absent on a stored doc = the defaults (off). */
  inboundCalls: InboundCallsSettings;
  /** Outbound calls + recording (Stage 3). Absent on a stored doc = the defaults. */
  calls: CallsSettings;
  updatedAt: IsoTimestamp;
  updatedBy: string;
  updatedByName: string | null;
}

export const SPEED_TO_LEAD_DEFAULTS = {
  samEnabled: false,
  speedToLeadEnabled: false,
  botConfigId: null,
  respectStaffedHours: true,
  fallbackMinutes: 5,
  qualityCallSeconds: 60,
  noAnswerAction: 'hang_up',
  retryAfterMinutes: null,
  staffRingSeconds: 25,
  roundTwoDelaySeconds: 120,
  maxRounds: 2,
} as const;

// ---------------------------------------------------------------------------
// number pools
// ---------------------------------------------------------------------------

/** Who is told when nobody can say which club an unknown texter means. */
export interface PoolGroupStaff {
  name: string;
  /** E.164. */
  mobile: string;
}

export const MAX_CLUBS_PER_POOL = 20;

export interface NumberPool {
  id: string;
  name: string;
  /** The brand account every club in the pool belongs to. */
  brandAccountId: string;
  clubAccountIds: string[];
  /** E.164 numbers, each registered in messagingNumbers as Vonage. A dedicated number is a pool of one club. */
  numbers: string[];
  groupStaff: PoolGroupStaff[];
  /**
   * Inbound call forwarding for an UNKNOWN caller on a shared pool number (we cannot tell which club they
   * want): E.164 or null. ⚠️ Setting it IS the pool's switch for forwarding unknown callers; null means
   * those callers get the fixed message. Falls back to `groupStaff` after it rings.
   */
  primaryNumber?: string | null;
  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
}

/** Pending state of an unknown texter on a shared pool number. Doc id `<poolId>__<phoneDigits>`. */
export interface PoolInboundSession {
  poolId: string;
  /** The texter, E.164. */
  phone: string;
  ourNumber: string;
  /** The texter's first message, handed to Sam once the club is known. */
  originalText: string;
  originalMessageId: string;
  /** The provider message id last processed for this session: a redelivered text does nothing twice. */
  lastMessageId: string;
  /** Times we have asked "which club?" (max 2). */
  asked: number;
  /** Missed matches (max 2). */
  misses: number;
  state: 'asking' | 'held' | 'matched' | 'escalated';
  /** 'held' only: the question was not sent because the bot gate would not allow it. */
  heldReason: string | null;
  clubAccountId: string | null;
  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
  expiresAt: Date;
}

/** The most recent intake lead for a phone in a pool, so a later text can be attributed. */
export interface PoolLeadLink {
  poolId: string;
  phone: string;
  accountId: string;
  leadSubmissionId: string;
  receivedAt: IsoTimestamp;
  expiresAt: Date;
}

/** "Do not contact" for a lead at ONE club: blocks texts and calls for that phone there. */
export interface DoNotContactRecord {
  accountId: string;
  phone: string;
  setAt: IsoTimestamp;
  setBy: string;
  setByName: string | null;
  note: string | null;
}

// ---------------------------------------------------------------------------
// the staff list as shown (Kyck settings + the club's portal page)
// ---------------------------------------------------------------------------

/** One row of the ordered call list. Name and role are READ from the club's team, never stored here. */
export interface StaffRow {
  teamMemberId: string;
  name: string;
  role: string;
  /** Whether the team member has a mobile number (the number itself is never sent to the portal). */
  hasMobile: boolean;
  receivesLeadCalls: boolean;
  /** In the saved staff list (as opposed to a team member offered for adding). */
  listed: boolean;
  /** Listed but no longer on the club's team: skipped at call time, shown with a visible warning. */
  removed: boolean;
}

// ---------------------------------------------------------------------------
// inbound calls (Stage 2.5): the call log
// ---------------------------------------------------------------------------

export type InboundCallOutcome =
  | 'answered_primary'
  | 'answered_staff'
  | 'no_answer'
  | 'out_of_hours'
  | 'disabled'
  | 'unrouted';

/** One ring target, in order. 'primary' is the club's (or the pool's) primary number. */
export interface InboundCallLeg {
  kind: 'primary' | 'staff' | 'group_staff';
  /** E.164. */
  number: string;
  /** BusinessProfile.team[] id for 'staff'; null otherwise. */
  teamMemberId: string | null;
}

/**
 * `leadInboundCalls/{callUuid}`: the receipt (create() once per Vonage call uuid, so a retried webhook never
 * rings twice) AND the call log. (Stage 3B added the recording fields.) The caller's number is stored
 * (staff need to call back) and is masked in every log line.
 */
export interface LeadInboundCall {
  callUuid: string;
  conversationUuid: string | null;
  accountId: string | null;
  poolId: string | null;
  callerPhone: string;
  ourNumber: string;
  callerKnown: boolean;
  outcome: InboundCallOutcome;
  /** Planned ring order; empty when nothing rings. */
  legs: InboundCallLeg[];
  /** The whisper played to whoever answers (no key press). */
  whisper: string | null;
  /** What the caller hears if nothing is answered. */
  fixedMessage: string;
  /** Seconds each target rings (from the plan). */
  ringSeconds: number;
  /** The caller's conversation, when they are a known lead (for the thread note). */
  conversationId: string | null;
  /** The NCCO returned to the answer webhook, stored so a retry replays it exactly. */
  answerNcco: unknown[];
  /** 'primary' or a teamMemberId or 'group_staff', once somebody answers. */
  answeredBy: string | null;
  ringStartedAt: IsoTimestamp | null;
  /** ⚠️ Set ONLY when a person pressed a key to take the call (never when a leg merely picked up). */
  answeredAt: IsoTimestamp | null;
  /**
   * What happened to each ringing leg, keyed by leg index, decided ONCE (a transaction): `accepted` (a key was
   * pressed) or `declined` (picked up but not accepted, or hung up). `by` says which path decided it, so the
   * other path stays quiet and a replayed event is idempotent.
   */
  legDecisions: Record<string, { result: 'accepted' | 'declined'; by: 'dtmf' | 'event'; transfer?: 'ok' | 'failed' }>;
  endedAt: IsoTimestamp | null;
  durationSeconds: number | null;
  /** Missed-call follow-up for a known caller (note + Sam's text, or a staff alert). */
  followUp: 'none' | 'pending' | 'claimed' | 'done';
  followUpClaimedAt: IsoTimestamp | null;
  // ---- recording (Stage 3B). ON by default; the settings are snapshotted so a later change never rewrites a call. ----
  recordCalls: boolean;
  transcribeCalls: boolean;
  recordingRetentionDays: number;
  /**
   * ⚠️ A recording is stored ONLY for a call a person accepted with a key press (answeredAt set). One for a call
   * nobody accepted is `discarded_unaccepted`: never downloaded, never stored.
   */
  recording: LeadCallRecording | null;
  transcript: LeadCallTranscript | null;
  /** Stage 4B (see LeadCall): absent on older docs. */
  summary?: LeadCallSummary | null;
  callOutcome?: LeadCallOutcome | null;
  summaryDueAt?: IsoTimestamp | null;
  mediaNextAt: IsoTimestamp | null;
  recordingDeleteAfter: IsoTimestamp | null;
  receivedAt: IsoTimestamp;
  /** When the transcript and summary TEXT of this call is deleted (call + 730 days). Stage 4D. Absent on older docs until media is stored. */
  textDeleteAfter?: IsoTimestamp | null;
  /** Real Timestamp for a Firestore TTL policy (24 months + 7 days since Stage 4D; was 30 days). */
  expiresAt: Date;
}
